import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Upload, ArrowRight } from "lucide-react";

const CATEGORIES = [
  "Transportation Leadership",
  "Community Impact",
  "Workforce Development",
  "Strategic Partner",
  "Innovation Award",
  "Emerging Leader",
  "Lifetime Achievement",
];

export default function HallOfFame() {
  const [form, setForm] = useState({
    nominee_name: "", organization: "", category: CATEGORIES[0], reason: "",
    linkedin: "", website: "", photo_url: "", supporting_doc_url: "",
    nominator_name: "", nominator_email: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/nominations", form);
      toast.success("Nomination submitted. Confirmation sent to your email.");
      setForm({
        nominee_name: "", organization: "", category: CATEGORIES[0], reason: "",
        linkedin: "", website: "", photo_url: "", supporting_doc_url: "",
        nominator_name: "", nominator_email: "",
      });
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Could not submit nomination.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="awards"
      data-testid="hall-of-fame-section"
      className="relative py-24 md:py-32 bg-aawta-charcoal scroll-mt-nav overflow-hidden"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-overline text-aawta-pink mb-4">HALL OF FAME</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-white tracking-tight">
              Honoring the architects<br />of <em className="italic text-aawta-pink">change</em>.
            </h2>
          </div>
        </div>

        {/* Inaugural Categories Announcement */}
        <div
          data-testid="hof-inaugural-announcement"
          className="relative border border-aawta-pink/30 bg-gradient-to-br from-aawta-pink/5 via-transparent to-aawta-blue/10 p-8 md:p-14 mb-20 overflow-hidden"
        >
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-aawta-pink/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-aawta-blue/15 blur-3xl pointer-events-none" />
          <div className="relative grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 bg-aawta-pink/15 border border-aawta-pink/40 text-aawta-pink text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 mb-5">
                <span className="w-1.5 h-1.5 bg-aawta-pink rounded-full animate-pulse" /> Inaugural Awards
              </div>
              <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-light text-white leading-[1.1]">
                Coming <em className="italic text-aawta-pink">January 2027.</em>
              </h3>
              <p className="text-white/75 mt-5 md:text-lg leading-relaxed max-w-2xl">
                2026 is our <strong className="text-white">first year</strong> awarding these categories.
                Winners will be selected from your nominations and recognized live at the December 2026
                gala. Profiles, bios, and photos of every recipient will go live here in January 2027
                &mdash; becoming the founding class of the AAWTA Hall of Fame.
              </p>
              <p className="text-aawta-pink mt-6 text-sm md:text-base font-medium">
                Place your nominations now &mdash; help us decide the face of each category.
              </p>
              <a
                href="#nominate"
                data-testid="hof-nominate-cta"
                className="btn-pink mt-7"
              >
                NOMINATE A LEADER <span className="ml-1">→</span>
              </a>
            </div>
            <div className="md:col-span-5">
              <p className="text-overline text-white/40 mb-4">2026 INAUGURAL CATEGORIES</p>
              <ul className="space-y-2.5">
                {CATEGORIES.map((c, i) => (
                  <li key={c} className="flex items-center gap-3 text-white/85 text-sm">
                    <span className="text-aawta-pink/60 font-mono text-xs w-6">0{i + 1}</span>
                    <span className="flex-1">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Nomination Portal */}
        <div id="nominate" className="grid md:grid-cols-12 gap-10 border-t border-white/10 pt-16 scroll-mt-nav">
          <div className="md:col-span-5">
            <p className="text-overline text-aawta-pink mb-4">2026 NOMINATIONS OPEN</p>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-light text-white tracking-tight leading-tight">
              Nominate a leader<br /> driving the industry.
            </h3>
            <p className="text-white/60 mt-6 leading-relaxed">
              Recognize the people and organizations shaping the future of trucking, logistics, and
              workforce development. Nominations close November 15, 2026.
            </p>
            <ul className="mt-8 space-y-2">
              {CATEGORIES.map((c) => (
                <li key={c} className="text-sm text-white/70 flex gap-3 items-center">
                  <span className="w-6 h-px bg-aawta-pink" /> {c}
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={submit} data-testid="nomination-form" className="md:col-span-7 glass-panel p-8 md:p-10 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="label-luxe">Nominee Name *</label>
                <input data-testid="nom-nominee-name" required className="input-luxe" value={form.nominee_name} onChange={handle("nominee_name")} />
              </div>
              <div>
                <label className="label-luxe">Organization</label>
                <input data-testid="nom-organization" className="input-luxe" value={form.organization} onChange={handle("organization")} />
              </div>
            </div>

            <div>
              <label className="label-luxe">Award Category *</label>
              <select data-testid="nom-category" required className="input-luxe bg-aawta-jet" value={form.category} onChange={handle("category")}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <label className="label-luxe">Reason for Nomination *</label>
              <textarea data-testid="nom-reason" required rows={4} className="input-luxe resize-none" value={form.reason} onChange={handle("reason")} />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="label-luxe">LinkedIn URL</label>
                <input data-testid="nom-linkedin" className="input-luxe" value={form.linkedin} onChange={handle("linkedin")} />
              </div>
              <div>
                <label className="label-luxe">Website</label>
                <input data-testid="nom-website" className="input-luxe" value={form.website} onChange={handle("website")} />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="label-luxe flex items-center gap-2"><Upload size={12} /> Photo URL</label>
                <input data-testid="nom-photo-url" placeholder="https://..." className="input-luxe" value={form.photo_url} onChange={handle("photo_url")} />
              </div>
              <div>
                <label className="label-luxe flex items-center gap-2"><Upload size={12} /> Supporting Document URL</label>
                <input data-testid="nom-doc-url" placeholder="https://..." className="input-luxe" value={form.supporting_doc_url} onChange={handle("supporting_doc_url")} />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 pt-2 border-t border-white/10 mt-2">
              <div>
                <label className="label-luxe">Your Name *</label>
                <input data-testid="nom-your-name" required className="input-luxe" value={form.nominator_name} onChange={handle("nominator_name")} />
              </div>
              <div>
                <label className="label-luxe">Your Email *</label>
                <input data-testid="nom-your-email" required type="email" className="input-luxe" value={form.nominator_email} onChange={handle("nominator_email")} />
              </div>
            </div>

            <button
              type="submit"
              data-testid="nom-submit-btn"
              disabled={submitting}
              className="btn-pink w-full mt-4 disabled:opacity-50"
            >
              {submitting ? "SUBMITTING..." : "SUBMIT NOMINATION"} <ArrowRight size={14} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
